import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Rss, Plus, Trash2, Lock, Loader2, ExternalLink } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function CustomFeeds() {
  const { user } = useAuth();
  const [feeds, setFeeds] = useState([]);
  const [allowed, setAllowed] = useState(false);
  const [maxFeeds, setMaxFeeds] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState("");
  const [label, setLabel] = useState("");
  const [removingId, setRemovingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rss/feeds");
      setFeeds(data.feeds || []);
      setAllowed(!!data.allowed);
      setMaxFeeds(data.max);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load your feeds.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setBusy(true);
    try {
      await api.post("/rss/feeds", { url: url.trim(), label: label.trim() || undefined });
      toast.success("Feed added — it'll show up in your next feed refresh.");
      setUrl(""); setLabel("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't add that feed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    setRemovingId(id);
    try {
      await api.delete(`/rss/feeds/${id}`);
      toast.success("Feed removed.");
      setFeeds((f) => f.filter((x) => x.id !== id));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRemovingId(null);
    }
  };

  const atLimit = maxFeeds !== -1 && feeds.length >= maxFeeds;

  return (
    <div className="space-y-8" data-testid="custom-feeds-page">
      <header>
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-2">
          Sources
        </div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-[-0.03em] font-medium leading-tight">
          Your custom <span className="text-gradient">RSS feeds</span>
        </h1>
        <p className="text-base text-gray-700 leading-relaxed mt-4 max-w-2xl">
          Add the sources you actually care about — a favorite Substack, a competitor&apos;s blog, an
          industry newsletter, a subreddit RSS. Stories from your custom feeds are pulled every
          15 minutes and mixed into your personal feed alongside our curated niches.
        </p>
      </header>

      {!allowed ? (
        <LockedState />
      ) : (
        <>
          {/* Add form */}
          <div className="rounded-2xl border border-[var(--border)] bg-white p-6 md:p-8" data-testid="add-feed-card">
            <div className="flex items-center gap-2 mb-4">
              <Plus size={16} strokeWidth={1.8} className="text-[#7C3AED]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gray-600">
                Add a feed
              </span>
            </div>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-[2fr,1fr,auto] gap-3 items-end">
              <Field label="RSS or Atom URL">
                <input
                  required
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/feed.xml"
                  className="input"
                  data-testid="feed-url-input"
                  disabled={busy || atLimit}
                />
              </Field>
              <Field label="Label (optional)">
                <input
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Ben Thompson"
                  className="input"
                  data-testid="feed-label-input"
                  disabled={busy || atLimit}
                />
              </Field>
              <button
                type="submit"
                disabled={busy || atLimit}
                data-testid="feed-add-submit"
                className="inline-flex items-center gap-2 justify-center bg-black text-white hover:bg-gray-900 disabled:opacity-60 rounded-xl px-5 py-3 font-medium text-sm transition-colors h-[46px]"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Rss size={14} strokeWidth={1.8} />}
                Add feed
              </button>
            </form>
            <div className="mt-3 text-[11px] text-gray-500 leading-relaxed" data-testid="feed-count-line">
              {atLimit ? (
                <>
                  You&apos;ve reached your feed limit ({maxFeeds}).{" "}
                  {maxFeeds !== -1 && (
                    <Link to="/pricing" className="underline hover:text-black">
                      Upgrade to Studio for unlimited feeds →
                    </Link>
                  )}
                </>
              ) : (
                <>
                  {feeds.length} of {maxFeeds === -1 ? "∞" : maxFeeds} feeds used.{" "}
                  We probe every URL before saving to make sure it&apos;s a valid RSS/Atom source.
                </>
              )}
            </div>
          </div>

          {/* Feed list */}
          <div className="rounded-2xl border border-[var(--border)] bg-white overflow-hidden" data-testid="feed-list">
            {loading ? (
              <div className="p-8 flex items-center gap-3 text-gray-500">
                <Loader2 size={16} className="animate-spin" />
                <span className="text-sm">Loading your feeds…</span>
              </div>
            ) : feeds.length === 0 ? (
              <div className="p-8 text-center">
                <Rss size={26} strokeWidth={1.5} className="mx-auto text-gray-300" />
                <div className="mt-3 font-display text-lg tracking-tight">No custom feeds yet</div>
                <div className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
                  Paste any RSS or Atom URL above to add it to your personalized feed.
                </div>
              </div>
            ) : (
              feeds.map((f) => (
                <div key={f.id} className="flex items-center gap-4 px-5 py-4 border-b border-[var(--border)] last:border-0" data-testid={`feed-row-${f.id}`}>
                  <div className="icon-tile shrink-0" style={{ background: "#EFE6FE", width: 32, height: 32, borderRadius: 8 }}>
                    <Rss size={15} strokeWidth={1.8} color="#7C3AED" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-[15px] tracking-tight leading-tight truncate">
                      {f.label || f.source || f.url}
                    </div>
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-0.5 text-[11px] font-mono uppercase tracking-widest text-gray-500 hover:text-black inline-flex items-center gap-1 truncate max-w-full"
                    >
                      {f.url.replace(/^https?:\/\//, "").slice(0, 60)}
                      <ExternalLink size={9} />
                    </a>
                  </div>
                  <button
                    onClick={() => remove(f.id)}
                    disabled={removingId === f.id}
                    data-testid={`feed-remove-${f.id}`}
                    className="text-gray-400 hover:text-red-600 disabled:opacity-60 transition-colors p-2"
                    aria-label="Remove feed"
                  >
                    {removingId === f.id
                      ? <Loader2 size={15} className="animate-spin" />
                      : <Trash2 size={15} strokeWidth={1.8} />}
                  </button>
                </div>
              ))
            )}
          </div>
        </>
      )}

      <style>{`
        .input {
          width: 100%;
          background: #FAF7F1;
          border: 1px solid rgba(0,0,0,0.10);
          border-radius: 12px;
          padding: 11px 13px;
          font-size: 14px;
          color: #111;
          height: 46px;
          transition: border-color .15s ease, background .15s ease;
        }
        .input:focus { outline: none; border-color: #111; background: #fff; }
        .input:disabled { opacity: 0.6; cursor: not-allowed; }
      `}</style>
    </div>
  );
}

function LockedState() {
  return (
    <div className="rounded-2xl border border-[var(--border)] bg-white p-10 text-center" data-testid="custom-feeds-locked">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#EFE6FE] mb-4">
        <Lock size={26} strokeWidth={1.8} color="#7C3AED" />
      </div>
      <h2 className="font-display text-2xl tracking-tight font-medium">
        Custom RSS is a Pro feature
      </h2>
      <p className="text-sm text-gray-600 leading-relaxed mt-3 max-w-md mx-auto">
        Pro subscribers can add up to 15 of their own RSS or Atom feeds. Studio subscribers get unlimited. Bring your favorite Substack, competitor blog, or industry newsletter into your Fount feed.
      </p>
      <Link
        to="/pricing"
        data-testid="custom-feeds-upgrade-cta"
        className="mt-6 inline-flex items-center gap-2 bg-black text-white hover:bg-gray-900 rounded-xl px-6 py-3 font-medium text-sm transition-colors"
      >
        See paid plans →
      </Link>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gray-600 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
